import React, { useEffect, useState } from "react";

const DualScreenDisplay = () => {
  const [payload, setPayload] = useState({
    type: "welcome",
    restaurant: { name: "MOO Sync" },
    timestamp: Date.now(),
    items: [],
  });

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel("moo-dual-screen");
    const fitToScreen = () => {
      try {
        const l = window.screen?.availLeft ?? 0;
        const t = window.screen?.availTop ?? 0;
        const w = window.screen?.availWidth ?? window.innerWidth;
        const h = window.screen?.availHeight ?? window.innerHeight;
        window.moveTo(l, t);
        window.resizeTo(w, h);
      } catch (_) {}
    };

    channel.onmessage = (event) => {
      const data = event.data || {};
      switch (data.type) {
        case "cart-update":
          setPayload({ ...data, type: "cart" });
          break;
        case "enter-fullscreen":
          try {
            document.documentElement.requestFullscreen().catch(() => {});
          } catch (_) {}
          break;
        case "geometry":
          if (data.geometry) {
            const { left, top, width, height } = data.geometry;
            try {
              window.moveTo(left || 0, top || 0);
              window.resizeTo(width || window.innerWidth, height || window.innerHeight);
            } catch (_) {}
            // Si après moveTo on reste très à gauche, tenter moveBy pour passer sur l'écran étendu
            try {
              if (left && window.screenX < left - 20) {
                window.moveBy(left, 0);
              }
            } catch (_) {}
          }
          break;
        case "fit-screen":
          fitToScreen();
          break;
        default:
          setPayload({ ...data, type: "welcome" });
      }
    };
    // Essayer de passer en plein écran et forcer l'overflow hidden au montage
    try {
      document.documentElement.requestFullscreen().catch(() => {});
    } catch (_) {}
    fitToScreen();
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyMargin = document.body.style.margin;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.margin = "0";
    return () => {
      channel.close();
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.margin = prevBodyMargin;
    };
  }, []);

  const isCart =
    payload.type === "cart" &&
    Array.isArray(payload.items) &&
    payload.items.length > 0;

  const restaurantName = payload.restaurant?.name || "MOO Sync";
  const formattedTotal = (value) =>
    `${Number(value || 0).toLocaleString("fr-FR")} F`;
  const isCompact = (payload.items?.length || 0) > 8;
  const useTwoCols = (payload.items?.length || 0) > 14;

  return (
    <div
      className="min-h-screen h-screen w-screen overflow-hidden flex items-center justify-center px-6"
      style={{
        background: "radial-gradient(circle at 20% 20%, #122142, #050b1b 60%)",
        color: "#e8ecf5",
        fontFamily: "Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-white/5" />
        <div className="absolute top-[-10%] right-[-10%] w-64 h-64 rounded-full blur-3xl opacity-20 bg-amber-500" />
        <div className="absolute bottom-[-15%] left-[-10%] w-[22rem] h-[22rem] rounded-full blur-3xl opacity-20 bg-blue-500" />
      </div>

      <div className="relative z-10 w-full max-w-5xl">
        {!isCart ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-10 text-center shadow-2xl shadow-black/40">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-20 w-20 rounded-3xl bg-white/10 border border-white/10 flex items-center justify-center shadow-lg shadow-black/30">
                <img
                  src="/moo-sync-insigne-logo.png"
                  alt="MOO Sync"
                  className="h-14 w-14 object-contain"
                />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                Affichage Client
              </p>
              <h1 className="text-3xl font-bold">{restaurantName}</h1>
              <p className="text-white/70 text-lg">
                Bienvenue ! Votre commande s’affichera ici.
              </p>
              <p className="text-xs uppercase tracking-[0.25em] text-white/60 mt-2">
                Conçu par <span className="font-semibold text-white">Djizef SARL</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur shadow-2xl shadow-black/40 p-8 space-y-6 max-h-[90vh] overflow-hidden w-full">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  Affichage Client
                </p>
                <h2 className="text-2xl font-bold">{restaurantName}</h2>
                <p className="text-white/70 text-sm mt-1">
                  {payload.tableLabel || "Commande en cours"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/60">Mis à jour</p>
                <p className="font-semibold">
                  {new Date(payload.timestamp || Date.now()).toLocaleTimeString(
                    "fr-FR",
                  )}
                </p>
              </div>
            </div>

            <div
              className={`rounded-2xl border border-white/10 bg-white/5 p-4 ${
                useTwoCols ? "grid grid-cols-2 gap-3" : "flex flex-col"
              } ${
                isCompact ? "text-sm leading-tight" : "text-base"
              }`}
            >
              {payload.items.map((item, idx) => {
                const variationLabel =
                  item.variationLabel ||
                  item.variation_label ||
                  "";
                const extrasSummary =
                  Array.isArray(item.extras) && item.extras.length > 0
                    ? item.extras
                        .map(
                          (ex) =>
                            `+ ${ex.quantity}x ${ex.name} (${formattedTotal(
                              (ex.price || 0) * (ex.quantity || 1),
                            )})`,
                        )
                        .join(isCompact ? " · " : "\n")
                    : null;
                return (
                  <div
                    key={`${item.name}-${idx}`}
                    className={`flex flex-col pb-3 border-b border-white/5 last:border-0 last:pb-0 ${
                      useTwoCols ? "min-h-[60px]" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className={`${isCompact ? "text-base" : "text-lg"} font-semibold`}>
                        {item.quantity}x {item.name}
                      </div>
                      <div className={`${isCompact ? "text-base" : "text-lg"} font-semibold text-amber-300`}>
                        {formattedTotal(item.total)}
                      </div>
                    </div>
                    {variationLabel && (
                      <div
                        className={`pl-2 text-emerald-200 ${
                          isCompact ? "text-xs" : "text-sm"
                        }`}
                      >
                        {variationLabel}
                      </div>
                    )}
                    {extrasSummary && (
                      <div
                        className={`pl-2 text-white/70 ${
                          isCompact ? "text-xs" : "text-sm"
                        } whitespace-pre-line`}
                      >
                        {extrasSummary}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex justify-between items-center">
                <div className="text-sm text-white/70">Sous-total</div>
                <div className="text-xl font-semibold text-white">
                  {formattedTotal(payload.subtotal)}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex justify-between items-center">
                <div className="text-sm text-white/70">Total</div>
                <div className="text-3xl font-bold text-emerald-300">
                  {formattedTotal(payload.total)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DualScreenDisplay;
